// WhyRankridgeNEET.jsx
// Image-free grid layout (content-only) for Rankridge NEET Long-Term Coaching

import React from "react";

function FeatureCard({ index, title, bullets }) {
  return (
    <div className="group rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#20b2aa]/10 text-[#20b2aa] font-bold">
          {index}
        </span>
        <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
      </div>
      <ul className="mt-4 list-disc list-inside space-y-2 text-neutral-700 leading-relaxed">
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

export default function WhyRankridgeNEET({
  phone = "+91 99 59 271 353",
  siteUrl = "https://www.rankridge.com",
  onApply,
}) {
  const features = [
    {
      title: "India’s Top-Ranked NEET Faculty",
      bullets: [
        "Mentors from AIIMS, IITs, and leading medical colleges",
        "Special focus on Physics & Chemistry — the real game changers",
        "Physics: Concept visualization + numerical strategies",
        "Chemistry: Mechanism-based teaching + NCERT-perfect approach",
      ],
    },
    {
      title: "Exclusive NEET-Specific Study Material",
      bullets: [
        "NCERT-aligned notes with topic-wise PYQs",
        "Chapter-wise mock tests & concept boosters",
        "Quick Revision Booklets",
        "Final Sprint Revision Modules for the last lap",
      ],
    },
    {
      title: "Proven NEET Track Record",
      bullets: [
        "1200+ NEET selections in the last 3 years",
        "20+ students scoring above 680 marks",
        "Consistent state toppers & MBBS admissions into AIIMS, JIPMER, and government medical colleges",
      ],
    },
    {
      title: "Personalized Academic Focus",
      bullets: [
        "Daily Practice Problems (DPPs) with detailed solutions",
        "Regular doubt clearance sessions by experts",
        "One-on-one mentoring with micro-level performance analysis",
      ],
    },
    {
      title: "Best-in-Class Learning Environment",
      bullets: [
        "Air-conditioned digital classrooms with smartboard technology",
        "Small batch sizes: 35–45 students per section",
        "Weekly motivational talks and memory-building techniques",
        "“Rankridge classrooms feel like mission control — every class fuels your dream.”",
      ],
    },
    {
      title: "World-Class Hostel Facility with Complete Care",
      bullets: [
        "Separate hostels for boys and girls",
        "24/7 security and academic supervision",
        "Nutritious meals (veg & non-veg), fresh fruits, and RO water",
        "Daily monitoring by wardens & academic mentors",
      ],
    },
    {
      title: "Special Attention to Physics & Chemistry",
      bullets: [
        "Physics: Animation-based concepts, real-life experiments, speed-accuracy training",
        "Chemistry: Physical, Organic & Inorganic modules with shortcut techniques, flowcharts, and concept mapping",
      ],
    },
    {
      title: "Online + Offline NEET Coaching Integration",
      bullets: [
        "Live & recorded classes for anytime revision",
        "Blended learning for flexibility + classroom discipline",
      ],
    },
  ];

  return (
    <section className="bg-white py-2 sm:py-2" id="rankridge-neet">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {/* Heading */}
        <header className="text-center  mx-auto">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#20b2aa]/10 px-3 py-1 text-xs font-medium tracking-wide text-[#20b2aa]">
            Best NEET Long-Term Coaching in Hyderabad
          </p>
          <h2 className="mt-3 text-4xl font-extrabold tracking-tight text-neutral-900 sm:text-5xl">
            Welcome to Hyderabad’s Premier NEET Preparation Hub
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-600">
            At Rankridge College, we turn medical dreams into reality with trusted, result-driven long-term NEET coaching programs. Designed for aspirants aiming for AIIMS, JIPMER, and top government medical colleges, our courses blend NCERT precision, expert faculty, and stress-free learning.
          </p>
        </header>

        {/* Grid of content-only feature cards */}
        <div className="mt-1 grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-2">
          {features.map((f, i) => (
            <FeatureCard key={i} index={i + 1} title={f.title} bullets={f.bullets} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 rounded-3xl p-1 bg-gradient-to-r from-[#20b2aa] via-teal-600 to-emerald-500">
          <div className="rounded-[22px] bg-white p-10 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-neutral-900">
              Rankridge – Hyderabad’s Most Trusted Long-Term Coaching for NEET & IIT-JEE
            </h3>
            <p className="mt-3 text-neutral-700">
              Your dream of becoming a doctor begins with expert guidance, stress-free preparation, and proven results.
            </p>
            <p className="mt-4 text-lg font-semibold text-[#20b2aa]">
              📞 Enroll Now – Limited Seats for 2025 Long-Term Batches
            </p>
            <p className="mt-1 text-neutral-600">
              Call: {phone} &nbsp;|&nbsp; Visit:{" "}
              <a className="underline decoration-dotted" href={siteUrl} target="_blank" rel="noreferrer">
                {siteUrl.replace(/^https?:\/\//, "")}
              </a>
            </p>
            <button
              onClick={onApply}
              className="mt-8 rounded-xl bg-[#20b2aa] px-8 py-4 font-semibold text-white shadow hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-teal-200"
              aria-label="Apply Now for Rankridge NEET Long-Term Coaching"
            >
              Apply Now
            </button>
          </div>
        </div>

        {/* About */}
        {/* <article className="mt-20 max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-semibold text-neutral-900">About Rankridge NEET Long-Term Coaching</h3>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            Rankridge College is Hyderabad’s most trusted destination for NEET long-term coaching, guiding aspirants to secure seats in AIIMS, JIPMER, and top government medical colleges. With expert faculty from AIIMS, IITs, and premier institutions, we combine NCERT fundamentals with advanced problem-solving strategies to ensure complete exam readiness.
          </p>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            Our proven track record of 1200+ selections in the past 3 years and 20+ students scoring above 680 marks reflects the dedication and precision of our programs. Students benefit from small batch sizes, daily practice problems, doubt-solving sessions, and one-on-one mentoring that make learning personal and effective.
          </p>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            Beyond academics, Rankridge ensures a holistic environment with stress-free study schedules, digital classrooms, nutritious food, and world-class hostel facilities. With both residential and day scholar options, along with online + offline integration, Rankridge creates the perfect ecosystem for medical aspirants to focus, prepare, and succeed.
          </p>
        </article> */}
      </div>
    </section>
  );
}
